import { Alert } from 'antd';
import { Fragment } from 'react';
import type { AuditEntryView } from './history.api';
import { TimelineSkeleton } from './TimelineSkeleton';
import type { HistoryState } from './use-history';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function formatValue(value: unknown): string {
  if (value === undefined) return '—';
  return typeof value === 'string' ? value : JSON.stringify(value);
}

/** Field-by-field comparison of an entry's before/after snapshots — null when there's nothing to show (an event with no snapshot). */
function AuditDiff({ before, after }: { before: unknown; after: unknown }) {
  const beforeObj = isRecord(before) ? before : {};
  const afterObj = isRecord(after) ? after : {};
  const keys = [...new Set([...Object.keys(beforeObj), ...Object.keys(afterObj)])]
    .filter((key) => JSON.stringify(beforeObj[key]) !== JSON.stringify(afterObj[key]))
    .sort();

  if (keys.length === 0) return null;

  return (
    <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
      {keys.map((key) => (
        <Fragment key={key}>
          <dt className="font-medium text-muted">{key}</dt>
          <dd className="text-fg">
            {key in beforeObj && (
              <>
                <span className="text-muted line-through">{formatValue(beforeObj[key])}</span>
                {' → '}
              </>
            )}
            <span>{formatValue(afterObj[key])}</span>
          </dd>
        </Fragment>
      ))}
    </dl>
  );
}

/** Who, when, what changed and reason, for one entry — used for a record's whole history, wherever it's shown (a drawer, a detail page tab). */
export function AuditTimeline({ state }: { state: HistoryState }) {
  return (
    <>
      {state.status === 'loading' && <TimelineSkeleton label="Loading history" />}
      {state.status === 'forbidden' && <Alert type="warning" title="You don't have permission to view this record's history." showIcon />}
      {state.status === 'error' && (
        <Alert type="error" title="Couldn't load history" description="Something went wrong loading this record's history. Try reloading." showIcon />
      )}
      {state.status === 'loaded' && state.entries.length === 0 && <p className="text-sm text-muted">No history yet.</p>}
      {state.status === 'loaded' && state.entries.length > 0 && (
        <ul className="flex flex-col gap-4">
          {state.entries.map((entry: AuditEntryView) => (
            <li key={entry.id} className="border-b border-line pb-4 last:border-0 last:pb-0">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-fg">{entry.actor ? entry.actor.name : 'System'}</span>
                <span className="text-xs text-muted">{new Date(entry.occurredAt).toLocaleString()}</span>
              </div>
              <p className="text-sm text-muted">
                {entry.action}
                {entry.onBehalfOf && <> — on behalf of {entry.onBehalfOf.name}</>}
                {entry.impersonatedUser && <> — acting as {entry.impersonatedUser.name}</>}
              </p>
              {entry.reason && <p className="mt-1 text-sm text-fg">{entry.reason}</p>}
              <AuditDiff before={entry.before} after={entry.after} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
